import { Button } from "@/components/ui/button";
import { Gauge } from "lucide-react";

/* ---------------- shared briefing line ---------------- */
export const Briefing = ({ c }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="engine-briefing">
    <span className="font-semibold text-slate-900">{c.name}</span>
    {c.meta ? <span className="text-slate-500"> — {c.meta}</span> : null}
    <div className="mt-2">{c.briefing}</div>
  </div>
);

/* ---------------- conversation mode shared bits ---------------- */
export const TensionMeter = ({ value }) => {
  const s = value <= 33 ? { w: "Calm", c: "#10b981" } : value <= 66 ? { w: "Tense", c: "#f59e0b" } : { w: "Heated", c: "#ef4444" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4 shadow-sm" data-testid="tension-meter">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Client tension</span>
        <span className="text-xs font-bold" style={{ color: s.c }} data-testid="tension-state">{s.w}</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, background: s.c }} data-testid="tension-bar" />
      </div>
    </div>
  );
};

export const PersonaHeader = ({ c, accent }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: accent }}>
      {(c.name || "?").trim()[0]}
    </div>
    <div>
      <div className="text-sm font-semibold text-slate-900">{c.name}</div>
      {c.meta && <div className="text-[11px] text-slate-500 -mt-0.5">{c.meta}</div>}
    </div>
  </div>
);

export const ClientBubble = ({ children }) => (
  <div className="max-w-[88%] rounded-2xl rounded-tl-sm bg-slate-100 text-slate-800 px-3.5 py-2.5 text-sm leading-relaxed" data-testid="conv-client-bubble">{children}</div>
);

// Animated "client is typing…" indicator shown briefly before a message appears.
export const TypingDots = () => (
  <div className="max-w-[60%] rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-3 flex items-center gap-1.5" data-testid="conv-typing">
    {[0, 1, 2].map((i) => (
      <span key={i} className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

// The learner's chosen reply, rendered as a right-aligned chat bubble after answering.
export const UserBubble = ({ children, accent }) => (
  <div className="flex justify-end">
    <div className="max-w-[88%] rounded-2xl rounded-tr-sm text-white px-3.5 py-2.5 text-sm leading-relaxed" style={{ background: accent }} data-testid="conv-user-bubble">{children}</div>
  </div>
);

// Inline pill showing how the reply moved the client's tension.
export const TensionImpact = ({ delta }) => {
  if (delta == null) return null;
  const eased = delta < 0;
  const rose = delta > 0;
  return (
    <div className={`flex justify-end mt-1`}>
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${eased ? "text-emerald-600" : rose ? "text-rose-600" : "text-slate-500"}`} data-testid="tension-impact">
        {eased ? "Tension eased ↓" : rose ? "Tension rose ↑" : "No change"}
      </span>
    </div>
  );
};

export const ConvShell = ({ beat, eyebrow, c, accent, Icon, checked, onPrimary, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm fade-up" data-testid={`${beat.key}-conv`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: accent }}>
      <Icon className="w-3.5 h-3.5" />{eyebrow}
    </div>
    <PersonaHeader c={c} accent={accent} />
    {children}
    <Button onClick={onPrimary} data-testid={`${beat.key}-primary-btn`} className="w-full text-white mt-4" style={{ background: accent }}>
      {checked ? (beat.last ? "See debrief →" : "Continue →") : beat.type === "multi" ? "Check" : "Send reply"}
    </Button>
  </div>
);
